import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { db, functions } from "../firebase";
import { collection, orderBy, query, where, onSnapshot } from "firebase/firestore";
import Masonry from "react-masonry-css";
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { Pagination, Zoom } from 'swiper/modules';
import Header from "../components/Header";
import Icon from "../components/Icon";
import { Filter, Check } from 'lucide-react';
import ProfileIcon from "../components/ProfileIcon";
import Button from "../components/Button";
import PhotoInfoOverlay from "../components/PhotoInfoOverlay";
import PhotoCard from "../components/PhotoCard";
import { useAdminMode } from "../context/AdminModeContext";
import { httpsCallable } from "firebase/functions";
import { useCurrentMemorial } from "../context/CurrentMemorialContext";
import { motion, AnimatePresence } from "framer-motion";
import { useMemorial } from "../hooks/useMemorial";

export default function MemorialImages() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState("desc");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showDescription, setShowDescription] = useState(false);
  const [showInfoOverlay, setShowInfoOverlay] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const swiperRef = useRef(null);
  const infoTimeoutRef = useRef(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const { adminMode } = useAdminMode();
  const { setCurrent } = useCurrentMemorial();
  const [isSelecting, setIsSelecting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const { memorial, isOwner } = useMemorial(slug);

  // Keep global context in sync so Navbar always has the correct slug
  useEffect(() => {
    if (memorial?.id) setCurrent({ slug, memorialId: memorial.id });
  }, [memorial?.id, slug, setCurrent]);

  useEffect(() => {
    if (!memorial?.id) return;
    const qImg = query(
      collection(db, "memorials", memorial.id, "memories"),
      where("type", "==", "image"),
      orderBy("createdAt", sortOrder)
    );
    const unsub = onSnapshot(qImg, (snap) => {
      setImages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [memorial?.id, sortOrder]);

  useEffect(() => () => { if (infoTimeoutRef.current) clearTimeout(infoTimeoutRef.current); }, []);

  useEffect(() => {
    if (isViewerOpen && swiperRef.current && selectedIndex !== null) {
      swiperRef.current.swiper.slideToLoop(selectedIndex, 0);
    }
  }, [selectedIndex, isViewerOpen]);

  const openViewer = (index) => {
    if (adminMode && isOwner && isSelecting) {
      const img = images[index];
      toggleSelect(img.id);
      return;
    }
    setSelectedIndex(index);
    setIsViewerOpen(true);
    setShowDescription(false);
    setShowInfoOverlay(true);
    infoTimeoutRef.current = setTimeout(() => setShowInfoOverlay(false), 3000);
  };
  const closeViewer = () => {
    setIsViewerOpen(false);
    setSelectedIndex(null);
    setShowDescription(false);
    setShowInfoOverlay(false);
    if (infoTimeoutRef.current) clearTimeout(infoTimeoutRef.current);
  };
  const handleViewerClick = () => {
    if (infoTimeoutRef.current) clearTimeout(infoTimeoutRef.current);
    setShowInfoOverlay(prev => !prev);
    if (!showInfoOverlay) {
      infoTimeoutRef.current = setTimeout(() => setShowInfoOverlay(false), 3000);
    }
  };

  const breakpointColumnsObj = { default: 3, 1024: 2, 768: 2 };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const deleteSelected = async () => {
    if (!memorial?.id || selectedIds.size === 0) return;
    const call = httpsCallable(functions, 'deleteMemories');
    await call({ memorialId: memorial.id, memoryIds: Array.from(selectedIds) });
    setImages(prev => prev.filter(img => !selectedIds.has(img.id)));
    setSelectedIds(new Set());
    setIsSelecting(false);
    setDeleteConfirm(false);
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setIsSelecting(false);
  };

  if (!memorial) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[var(--color-bg)]">
        <div className="w-8 h-8 border-2 border-[#56371F] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen text-Accent-Minde overflow-x-hidden" style={{ backgroundColor: 'var(--color-bg)' }}>
      <div className="fixed top-0 left-0 right-0 z-50">
        <Header title="Photo gallery">
          <div className="flex items-center gap-3">
            <>
              <div className="relative" style={{ zIndex: 60 }}>
                <button onClick={() => setDropdownOpen(p => !p)} className="h-11 w-11 rounded-full bg-[#44231E] flex items-center justify-center focus:outline-none">
                  <Filter size={22} color="#EFEBE0" strokeWidth={2} />
                </button>
              {dropdownOpen && (
                <div className="absolute right-0 mt-2 bg-white rounded-[16px] shadow-lg text-[#44231E] w-48 font-['Poppins'] overflow-hidden" style={{ zIndex: 60, border: '1px solid rgba(0,0,0,0.06)' }}>
                    <div onClick={() => { setSortOrder("desc"); setDropdownOpen(false); }} className={`px-5 py-3 hover:bg-[#F8F8F8] cursor-pointer flex justify-between items-center text-[16px] ${sortOrder === "desc" ? "bg-[#F8F8F8] font-medium" : "font-normal"}`}><span>Newest first</span>{sortOrder === "desc" && <Check size={15} color="#44231E" strokeWidth={2.5} />}</div>
                    <div onClick={() => { setSortOrder("asc"); setDropdownOpen(false); }} className={`px-5 py-3 hover:bg-[#F8F8F8] cursor-pointer flex justify-between items-center text-[16px] ${sortOrder === "asc" ? "bg-[#F8F8F8] font-medium" : "font-normal"}`}><span>Oldest first</span>{sortOrder === "asc" && <Check size={15} color="#44231E" strokeWidth={2.5} />}</div>
                  </div>
                )}
              </div>
              <button onClick={() => setViewMode(prev => prev === 'grid' ? 'list' : 'grid')} className="h-11 w-11 rounded-full bg-[#44231E] flex items-center justify-center focus:outline-none" title={viewMode === 'grid' ? 'List view' : 'Grid view'}>
                {viewMode === 'grid' ? (<Icon name="list" size={22} color="#EFEBE0" />) : (<Icon name="grid" size={22} color="#EFEBE0" />)}
              </button>
              <button onClick={() => navigate(`/m/${slug}/my-uploads`)} className="focus:outline-none"><ProfileIcon /></button>
            </>
          </div>
        </Header>
      </div>

      <div
        className="px-6 pb-24 overflow-y-auto"
        style={{
          paddingTop:
            adminMode && isOwner
              ? 160
              : 'max(76px, calc(68px + env(safe-area-inset-top, 0px)))',
        }}
      >
        {/* Glassy overlay for admin actions (always visible in admin mode) */}
        {adminMode && isOwner && (
          <div style={{ position: 'fixed', top: 72, left: 16, right: 16, zIndex: 40, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: 10, borderRadius: 30, background: 'rgba(255,255,255,0.35)', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}>
            <div>
              {isSelecting ? (
                <Button onClick={clearSelection}>Exit selection</Button>
              ) : (
                <Button onClick={() => setIsSelecting(true)}>Select</Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {deleteConfirm ? (
                <>
                  <span style={{ fontSize: 13, fontFamily: 'Poppins', color: '#44231E' }}>
                    Delete {selectedIds.size}?
                  </span>
                  <Button onClick={deleteSelected}>Yes</Button>
                  <Button onClick={() => setDeleteConfirm(false)}>No</Button>
                </>
              ) : (
                <Button variant="danger" onClick={() => setDeleteConfirm(true)} disabled={selectedIds.size === 0}>
                  {`Delete ${selectedIds.size} selected`}
                </Button>
              )}
            </div>
          </div>
        )}

        {viewMode === 'grid' ? (
          <Masonry
            breakpointCols={breakpointColumnsObj}
            className="flex"
            columnClassName="flex flex-col"
          >
            {images.map((img, index) => (
              <PhotoCard
                key={img.id || index}
                image={img}
                index={index}
                onClick={() => openViewer(index)}
                isSelected={adminMode && isOwner && selectedIds.has(img.id)}
              />
            ))}
          </Masonry>
        ) : (
          <div className="flex flex-col">
            {images.map((img, index) => (
              <PhotoCard
                key={img.id || index}
                image={img}
                index={index}
                onClick={() => openViewer(index)}
                isSelected={adminMode && isOwner && selectedIds.has(img.id)}
                plain
              />
            ))}
          </div>
        )}
        {images.length === 0 && (
          <p className="text-center mt-10 text-[14px] text-[#6A5A45] font-['Poppins']">
            No photos yet.
          </p>
        )}
      </div>

      <AnimatePresence>
      {isViewerOpen && (
        <motion.div 
          className="fixed inset-0 bg-black bg-opacity-80 z-[70]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Top controls: description toggle (if any) and X close */}
          <div className="absolute top-4 left-6 right-6 z-[80] flex items-center">
            {images[selectedIndex]?.text && (
              <button
                onClick={() => setShowDescription(!showDescription)}
                className="inline-flex items-center gap-3"
                style={{
                  padding: '10px 14px',
                  borderRadius: 999,
                  border: '1px solid rgba(255,255,255,0.24)',
                  background: 'linear-gradient(180deg, rgba(31,20,16,0.76) 0%, rgba(31,20,16,0.68) 100%)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                  backdropFilter: 'blur(16px) saturate(130%)',
                  WebkitBackdropFilter: 'blur(16px) saturate(130%)',
                }}
              >
                <span
                  style={{
                    color: '#FFFFFF',
                    fontSize: 15,
                    fontFamily: 'Poppins',
                    fontWeight: 500,
                    lineHeight: 1,
                    textShadow: '0 1px 2px rgba(0,0,0,0.35)',
                  }}
                >
                  {showDescription ? 'Close description' : 'Open description'}
                </span>
                <span
                  aria-hidden
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: 100,
                    background: 'rgba(255,255,255,0.14)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid rgba(255,255,255,0.28)',
                  }}
                >
                  <Icon
                    name={showDescription ? 'closeOverlay' : 'openOverlay'}
                    size={16}
                    color="#FFFFFF"
                  />
                </span>
              </button>
            )}
            <button
              onClick={closeViewer}
              className="ml-auto"
              style={{
                width: 44,
                height: 44,
                padding: 10,
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.35)',
                borderRadius: 100,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
              }}
            >
              <Icon name="xWhite" size={24} color="#FFFFFF" />
            </button>
          </div>
          {/* Description Overlay */}
          {showDescription && images[selectedIndex]?.text && (
            <div className="absolute top-[84px] left-0 right-0 z-[75] px-6">
              <div className="mx-auto">
                <div className="bg-white rounded-[20px] p-6 shadow-lg" style={{ width: '100%' }}>
                  <p className="text-[#000] text-[16px] font-['Poppins'] leading-[1.5] whitespace-pre-line">
                    {images[selectedIndex].text}
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="relative w-full h-full flex items-center justify-center" onClick={handleViewerClick}>
            <Swiper
              ref={swiperRef}
              modules={[Pagination, Zoom]}
              style={{ '--swiper-pagination-color': '#fff', '--swiper-pagination-bullet-inactive-color': '#aaa', '--swiper-pagination-bullet-inactive-opacity': '1', '--swiper-pagination-bullet-size': '6px', '--swiper-pagination-bottom': '46px' }}
              spaceBetween={0}
              slidesPerView={1}
              pagination={{ clickable: true }}
              zoom={true}
              loop={true}
              initialSlide={selectedIndex}
              className="h-full w-full"
              onSlideChange={(swiper) => { setSelectedIndex(swiper.realIndex); setShowInfoOverlay(true); if (infoTimeoutRef.current) clearTimeout(infoTimeoutRef.current); infoTimeoutRef.current = setTimeout(() => setShowInfoOverlay(false), 3000); }}
            >
              {images.map((img, index) => (
                <SwiperSlide key={img.id || index} className="relative flex items-center justify-center">
                  <div className="swiper-zoom-container flex items-center justify-center w-full h-full">
                    <img src={img.url} alt={`Fullscreen ${index + 1}`} className="block w-full h-full object-contain" />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
          {/* Info Overlay */}
          <div className={`absolute bottom-10 left-0 right-0 z-[75] px-6 transition-opacity duration-300 ease-in-out flex justify-center ${showInfoOverlay ? 'opacity-100' : 'opacity-0'}`}>
            {selectedIndex !== null && images[selectedIndex] && (
              <PhotoInfoOverlay
                author={(images[selectedIndex].authorName === 'Anonym' ? 'Anonymous' : images[selectedIndex].authorName) || 'Anonymous'}
                date={images[selectedIndex].createdAt?.toDate ? new Date(images[selectedIndex].createdAt.toDate()).toLocaleDateString('da-DK', { day: '2-digit', month: '2-digit', year: '2-digit' }) : ''}
              />
            )}
          </div>
        </motion.div>
      )}
      </AnimatePresence>
    </div>
  );
}


